import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { MovementPattern, PlanSlot, TrainingTemplate } from "@jilian/shared";
import {
  createDefaultTemplates,
  generateSlotsFromTargetMuscles,
  validateTrainingTemplate,
  evaluateExerciseEligibility,
  weeklyMuscleSets,
} from "@jilian/core";
import { EXERCISE_LIBRARY, EXERCISE_BY_ID } from "../data/exercises";
import { useSettings, useTrainingTemplates } from "../db/hooks";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../db/db";
import {
  deleteTrainingTemplate,
  reorderTrainingTemplates,
  resetTrainingTemplatesToDefault,
  saveTrainingTemplate,
  DEFAULT_INJURY,
} from "../db/repo";
import { Button, Card, RiskTag, SectionTitle, Segmented, Sheet, Toggle } from "../components/ui";
import { uuid } from "../lib/ids";
import { localDateString } from "../lib/timezone";

const TARGET_MUSCLE_OPTIONS = [
  { key: "胸", label: "胸部" },
  { key: "背", label: "背部" },
  { key: "肩", label: "肩部" },
  { key: "腿", label: "腿部" },
  { key: "二头", label: "二头" },
  { key: "三头", label: "三头" },
  { key: "核心", label: "核心" },
  { key: "臀", label: "臀部" },
];

const LARGE_MUSCLES = new Set(["胸大肌", "背阔肌", "股四头肌", "腘绳肌", "臀大肌", "胸", "背", "腿", "臀"]);

const DISPLAY_MUSCLES = ["胸大肌", "背阔肌", "三角肌", "肱二头肌", "肱三头肌", "股四头肌", "臀大肌", "腹直肌"];

function getMuscleInterval(muscle: string): { min: number; max: number; isLarge: boolean } {
  const isLarge = LARGE_MUSCLES.has(muscle);
  return isLarge ? { min: 10, max: 20, isLarge: true } : { min: 6, max: 14, isLarge: false };
}

const MOVEMENT_PATTERNS: { value: MovementPattern; label: string }[] = [
  { value: "horizontal_push", label: "水平推（推胸/夹胸）" },
  { value: "vertical_push", label: "垂直推（推肩）" },
  { value: "horizontal_pull", label: "水平拉（划船）" },
  { value: "vertical_pull", label: "垂直拉（下拉/引体）" },
  { value: "knee_extension", label: "伸膝（腿屈伸）" },
  { value: "knee_flexion", label: "屈膝（腿弯举）" },
  { value: "hip_abduction", label: "髋外展" },
  { value: "hip_adduction", label: "髋内收" },
  { value: "arm_curl", label: "屈臂（弯举）" },
  { value: "arm_extension", label: "伸臂（下压/臂屈伸）" },
  { value: "trunk_flexion", label: "核心（卷腹）" },
];

export function PlanEditor() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const targetId = searchParams.get("id");

  const settings = useSettings();
  const profileId = settings?.profile?.id ?? "user-default";
  const injury = settings?.injury ?? DEFAULT_INJURY;

  const dbTemplates = useTrainingTemplates(profileId);
  const templates = useMemo(() => {
    return dbTemplates && dbTemplates.length > 0 ? dbTemplates : createDefaultTemplates(profileId);
  }, [dbTemplates, profileId]);

  const [selectedId, setSelectedId] = useState<string>(targetId || templates[0]?.id || "");
  const [editTemplate, setEditTemplate] = useState<TrainingTemplate | null>(null);
  const [mode, setMode] = useState<"simple" | "advanced">("simple");
  const [selectedMuscles, setSelectedMuscles] = useState<string[]>(["胸", "三头"]);
  const [activeSlotIdx, setActiveSlotIdx] = useState<number | null>(null);
  const [showExercisePicker, setShowExercisePicker] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerFilter, setPickerFilter] = useState<string>("全部");
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // 查询近 7 天有效完成组
  const pastWeekSets = useLiveQuery(async () => {
    if (!profileId) return [];
    const sessions = await db.sessions.where("userId").equals(profileId).toArray();
    const validSessions = sessions.filter((s) => s.status === "completed" || s.status === "ended_early");
    if (validSessions.length === 0) return [];
    const sessionIds = validSessions.map((s) => s.id);
    const items = await db.exerciseItems.where("sessionId").anyOf(sessionIds).toArray();
    const itemMap = new Map(items.map((i) => [i.id, i.exerciseId]));
    const itemIds = items.map((i) => i.id);
    const sets = await db.sets.where("workoutExerciseId").anyOf(itemIds).toArray();
    return sets
      .filter((s) => s.status === "completed")
      .map((s) => ({
        exerciseId: itemMap.get(s.workoutExerciseId) ?? "",
        completedAt: s.completedAt ?? s.updatedAt,
        status: s.status,
      }));
  }, [profileId]);

  const totalSessionsCount = useLiveQuery(async () => {
    if (!profileId) return 0;
    return db.sessions.where("userId").equals(profileId).and((s) => s.status === "completed" || s.status === "ended_early").count();
  }, [profileId]);

  const todayStr = localDateString();

  const currentWeeklyMuscleSets = useMemo(() => {
    if (!pastWeekSets) return new Map<string, number>();
    const list = weeklyMuscleSets(pastWeekSets, EXERCISE_BY_ID, todayStr);
    return new Map(list.map((item) => [item.muscle, item.sets]));
  }, [pastWeekSets, todayStr]);

  // 当前正在编辑的模板对各肌群规划的组数增量
  const templatePlannedMuscleSets = useMemo(() => {
    const map = new Map<string, number>();
    if (!editTemplate) return map;
    for (const slot of editTemplate.slots) {
      const firstEx = slot.preferredIds[0] ? EXERCISE_BY_ID.get(slot.preferredIds[0]) : null;
      const muscles = firstEx?.primaryMuscles ?? (slot.muscle ? [slot.muscle] : []);
      for (const m of muscles) {
        map.set(m, (map.get(m) ?? 0) + (firstEx?.defaultSets ?? 3));
      }
    }
    return map;
  }, [editTemplate]);

  // 动作选择器过滤结果
  const filteredPickerExercises = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    return EXERCISE_LIBRARY.filter((e) => {
      if (pickerFilter !== "全部") {
        if (pickerFilter === "孤立" && e.compoundType !== "isolation") return false;
        if (pickerFilter === "复合" && e.compoundType !== "compound") return false;
        if (pickerFilter === "安全可用" && (e.lockedUntilMedicalClearance || !evaluateExerciseEligibility({ exercise: e, injury }).isEnabledForCurrentInjury)) return false;
      }
      if (!q) return true;
      return (
        e.nameZh.toLowerCase().includes(q) ||
        e.nameEn.toLowerCase().includes(q) ||
        e.primaryMuscles.some((m) => m.toLowerCase().includes(q))
      );
    });
  }, [pickerSearch, pickerFilter, injury]);

  // 同步选中项到本地可编辑状态
  useEffect(() => {
    const current = templates.find((t) => t.id === selectedId) || templates[0];
    if (current) {
      setSelectedId(current.id);
      setEditTemplate(JSON.parse(JSON.stringify(current)));
      setValidationErrors([]);
    }
  }, [selectedId, templates]);

  if (!editTemplate) {
    return (
      <div className="min-h-screen mx-auto max-w-[480px] px-4 pt-safe-top flex items-center justify-center text-tone-soft">
        加载训练计划…
      </div>
    );
  }

  const onUpdateField = <K extends keyof TrainingTemplate>(field: K, val: TrainingTemplate[K]) => {
    setEditTemplate((prev) => (prev ? { ...prev, [field]: val } : null));
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    if (!editTemplate) return;
    const { valid, errors } = validateTrainingTemplate(editTemplate, EXERCISE_LIBRARY);
    if (!valid) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors([]);
    await saveTrainingTemplate(editTemplate);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2500);
  };

  const handleResetDefaults = async () => {
    const defaults = await resetTrainingTemplatesToDefault(profileId);
    setConfirmReset(false);
    if (defaults.length > 0) {
      setSelectedId(defaults[0]!.id);
    }
  };

  const handleDeleteTemplate = async () => {
    if (templates.length <= 1) {
      alert("系统至少需要保留 1 个训练序列");
      return;
    }
    await deleteTrainingTemplate(editTemplate.id, profileId);
    setConfirmDelete(false);
    const rest = templates.filter((t) => t.id !== editTemplate.id);
    if (rest.length > 0) {
      setSelectedId(rest[0]!.id);
    }
  };

  const handleAddTemplate = async () => {
    const nextCode = String.fromCharCode(65 + templates.length); // A, B, C, D, E, F...
    const newTpl: TrainingTemplate = {
      id: uuid(),
      userId: profileId,
      code: nextCode,
      name: `新序列 ${nextCode}`,
      slots: [
        {
          id: `slot_${Date.now()}_1`,
          description: "自定义推胸动作",
          preferredIds: ["seated_machine_press"],
          muscle: "胸大肌",
          pattern: "horizontal_push",
          isolationOnly: false,
          dropWhenEmpty: true,
        },
      ],
      position: templates.length + 1,
      isOptionalLightDay: false,
      source: "custom",
      version: 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    await saveTrainingTemplate(newTpl);
    setSelectedId(newTpl.id);
  };

  const handleMoveTemplate = async (direction: "up" | "down") => {
    const idx = templates.findIndex((t) => t.id === editTemplate.id);
    if (idx === -1) return;
    if (direction === "up" && idx > 0) {
      const swapped = [...templates];
      const tmp = swapped[idx]!;
      swapped[idx] = swapped[idx - 1]!;
      swapped[idx - 1] = tmp;
      await reorderTrainingTemplates(swapped, profileId);
    } else if (direction === "down" && idx < templates.length - 1) {
      const swapped = [...templates];
      const tmp = swapped[idx]!;
      swapped[idx] = swapped[idx + 1]!;
      swapped[idx + 1] = tmp;
      await reorderTrainingTemplates(swapped, profileId);
    }
  };

  // 简单模式：根据部位生成槽位
  const handleSimpleGenerate = () => {
    if (selectedMuscles.length === 0) {
      setValidationErrors(["请至少勾选一个目标部位"]);
      return;
    }
    const generated = generateSlotsFromTargetMuscles(selectedMuscles, EXERCISE_LIBRARY);
    if (generated.length === 0) {
      setValidationErrors(["所选部位未找到合适动作"]);
      return;
    }
    setEditTemplate((prev) => (prev ? { ...prev, slots: generated } : null));
    setValidationErrors([]);
    setSaveSuccess(false);
  };

  // 高级模式槽位操作
  const handleAddSlot = () => {
    const newSlot: PlanSlot = {
      id: `slot_${Date.now()}_${editTemplate.slots.length + 1}`,
      description: "新增动作槽位",
      preferredIds: [],
      isolationOnly: Boolean(editTemplate.isOptionalLightDay),
      dropWhenEmpty: true,
    };
    setEditTemplate((prev) => (prev ? { ...prev, slots: [...prev.slots, newSlot] } : null));
    setSaveSuccess(false);
  };

  const handleRemoveSlot = (idx: number) => {
    setEditTemplate((prev) => {
      if (!prev) return null;
      const next = [...prev.slots];
      next.splice(idx, 1);
      return { ...prev, slots: next };
    });
    setSaveSuccess(false);
  };

  const handleMoveSlot = (idx: number, direction: "up" | "down") => {
    setEditTemplate((prev) => {
      if (!prev) return null;
      const next = [...prev.slots];
      if (direction === "up" && idx > 0) {
        const tmp = next[idx]!;
        next[idx] = next[idx - 1]!;
        next[idx - 1] = tmp;
      } else if (direction === "down" && idx < next.length - 1) {
        const tmp = next[idx]!;
        next[idx] = next[idx + 1]!;
        next[idx + 1] = tmp;
      }
      return { ...prev, slots: next };
    });
    setSaveSuccess(false);
  };

  const handleUpdateSlot = (idx: number, patch: Partial<PlanSlot>) => {
    setEditTemplate((prev) => {
      if (!prev) return null;
      const next = [...prev.slots];
      next[idx] = { ...next[idx]!, ...patch };
      return { ...prev, slots: next };
    });
    setSaveSuccess(false);
  };

  const openPicker = (idx: number) => {
    setActiveSlotIdx(idx);
    setPickerSearch("");
    setShowExercisePicker(true);
  };

  const currentSlot = activeSlotIdx !== null ? editTemplate.slots[activeSlotIdx] : null;

  const togglePreferredExercise = (exId: string) => {
    if (activeSlotIdx === null || !currentSlot) return;
    const exists = currentSlot.preferredIds.includes(exId);
    const nextIds = exists
      ? currentSlot.preferredIds.filter((id) => id !== exId)
      : [...currentSlot.preferredIds, exId];

    handleUpdateSlot(activeSlotIdx, { preferredIds: nextIds });
  };

  return (
    <div className="min-h-screen mx-auto max-w-[480px] px-4 pt-safe-top pb-12">
      {/* 顶部导航 */}
      <div className="flex items-center justify-between py-3 mb-2">
        <button
          onClick={() => nav(-1)}
          className="inline-flex items-center gap-1 text-tone-accent font-semibold text-sm active:opacity-70"
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          返回
        </button>
        <h1 className="text-[17px] font-bold text-tone-ink">序列与计划编辑</h1>
        <button
          onClick={() => setConfirmReset(true)}
          className="text-xs text-tone-warn font-semibold active:opacity-70"
        >
          恢复默认
        </button>
      </div>

      {/* 序列切换栏 */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none mb-3">
        {templates.map((t) => (
          <button
            key={t.id}
            onClick={() => setSelectedId(t.id)}
            className={`min-h-[38px] px-3.5 rounded-full text-xs font-bold whitespace-nowrap transition-all active:scale-95 flex items-center gap-1.5 ${
              selectedId === t.id
                ? "bg-tone-accent text-white shadow-sm shadow-tone-accent/25"
                : "bg-black/5 dark:bg-white/10 text-tone-soft hover:text-tone-ink"
            }`}
          >
            <span>序列 {t.code}</span>
            {t.isOptionalLightDay ? <span className="opacity-80 text-[10px] font-normal">(可选)</span> : null}
          </button>
        ))}
        <button
          onClick={() => void handleAddTemplate()}
          className="min-h-[38px] px-3 rounded-full text-xs font-bold text-tone-accent bg-tone-accent/10 whitespace-nowrap active:scale-95"
        >
          + 新增序列
        </button>
      </div>

      {/* 本周各肌群有效容量条 */}
      <Card className="p-3.5 mb-3.5 border border-tone-border shadow-sm space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span className="text-[13px] font-bold text-tone-ink">📊 本周各肌群有效组数</span>
            {(totalSessionsCount ?? 0) < 2 ? (
              <span className="text-[10px] text-tone-soft bg-black/5 dark:bg-white/10 px-1.5 py-0.5 rounded">
                数据积累中
              </span>
            ) : null}
          </div>
          <span className="text-[10px] text-tone-soft">一般循证参考，非医学结论</span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {DISPLAY_MUSCLES.map((muscle) => {
            const current = currentWeeklyMuscleSets.get(muscle) ?? 0;
            const planned = templatePlannedMuscleSets.get(muscle) ?? 0;
            const total = current + planned;
            const { min, max } = getMuscleInterval(muscle);

            const isInRange = total >= min && total <= max;
            const isHigh = total > max;

            return (
              <div
                key={muscle}
                className={`p-2 rounded-lg border text-xs transition-all ${
                  isInRange
                    ? "bg-tone-good/10 border-tone-good/30 text-tone-ink"
                    : isHigh
                      ? "bg-tone-warn/10 border-tone-warn/30 text-tone-ink"
                      : "bg-black/[0.02] dark:bg-white/[0.03] border-tone-border text-tone-soft"
                }`}
              >
                <div className="flex items-center justify-between font-bold">
                  <span>{muscle}</span>
                  <span
                    className={`text-[10px] px-1 py-0.2 rounded font-semibold ${
                      isInRange
                        ? "bg-tone-good/20 text-tone-good"
                        : isHigh
                          ? "bg-tone-warn/20 text-tone-warn"
                          : "text-tone-soft"
                    }`}
                  >
                    {isInRange ? "适宜" : isHigh ? "偏高" : "偏低"}
                  </span>
                </div>
                <div className="mt-1 flex items-baseline justify-between text-[11px]">
                  <span>
                    已练 <strong className="text-tone-ink font-bold">{current}</strong> 组
                    {planned > 0 && (
                      <span className="text-tone-accent font-semibold ml-1">+{planned}</span>
                    )}
                  </span>
                  <span className="opacity-70 font-caption">
                    参考 {min}–{max}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* 模板基础设置卡片 */}
      <Card className="p-4 mb-4 space-y-3.5 border border-tone-border shadow-sm">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold text-tone-soft">基本信息（位次 #{editTemplate.position}）</span>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => void handleMoveTemplate("up")}
              className="p-1.5 rounded-lg bg-black/5 dark:bg-white/10 text-tone-soft hover:text-tone-ink text-xs font-bold active:scale-90"
              title="前移轮转位次"
            >
              ▲ 上移
            </button>
            <button
              onClick={() => void handleMoveTemplate("down")}
              className="p-1.5 rounded-lg bg-black/5 dark:bg-white/10 text-tone-soft hover:text-tone-ink text-xs font-bold active:scale-90"
              title="后移轮转位次"
            >
              ▼ 下移
            </button>
            <button
              onClick={() => setConfirmDelete(true)}
              className="p-1.5 rounded-lg bg-tone-danger/10 text-tone-danger text-xs font-bold active:scale-90"
              title="删除此序列"
            >
              删除
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2.5">
          <div>
            <label className="text-[11px] font-semibold text-tone-soft mb-1 block">序列代号</label>
            <input
              value={editTemplate.code}
              onChange={(e) => onUpdateField("code", e.target.value.trim().toUpperCase())}
              maxLength={6}
              placeholder="A"
              className="w-full min-h-[42px] px-3 rounded-xl bg-black/5 dark:bg-white/10 text-tone-ink text-sm font-bold uppercase text-center"
            />
          </div>
          <div className="col-span-2">
            <label className="text-[11px] font-semibold text-tone-soft mb-1 block">序列名称</label>
            <input
              value={editTemplate.name}
              onChange={(e) => onUpdateField("name", e.target.value)}
              placeholder="如：胸 · 三头 · 腿屈伸"
              className="w-full min-h-[42px] px-3 rounded-xl bg-black/5 dark:bg-white/10 text-tone-ink text-sm font-semibold"
            />
          </div>
        </div>

        <div className="pt-1 border-t border-tone-border">
          <Toggle
            checked={editTemplate.isOptionalLightDay}
            onChange={(v) => onUpdateField("isOptionalLightDay", v)}
            label="设为可选轻训练日（E 日规则）"
          />
          <p className="text-[11px] text-tone-soft mt-1 leading-relaxed">
            可选轻训日不参与正式轮转；总组数上限为正式日的 70%，全部动作必须为孤立动作、RIR ≥ 2 且禁止力竭。
          </p>
        </div>
      </Card>

      {/* 编辑模式切换 */}
      <div className="mb-3">
        <Segmented
          value={mode}
          onChange={(v) => setMode(v as "simple" | "advanced")}
          options={[
            { value: "simple", label: "简单模式（部位生成）" },
            { value: "advanced", label: "高级模式（逐槽编辑）" },
          ]}
        />
      </div>

      {/* 简单模式 */}
      {mode === "simple" ? (
        <Card className="p-4 mb-4 space-y-3.5 border border-tone-border shadow-sm">
          <div>
            <div className="text-[14px] font-bold text-tone-ink">选择目标训练部位</div>
            <div className="text-[11px] text-tone-soft mt-0.5">
              系统将按「复合优先、孤立靠后」的生理规则自动生成标准动作槽位
            </div>
          </div>

          <div className="grid grid-cols-4 gap-2">
            {TARGET_MUSCLE_OPTIONS.map((m) => {
              const isSelected = selectedMuscles.includes(m.key);
              return (
                <button
                  key={m.key}
                  onClick={() =>
                    setSelectedMuscles((prev) =>
                      isSelected ? prev.filter((k) => k !== m.key) : [...prev, m.key],
                    )
                  }
                  className={`min-h-[42px] rounded-xl text-xs font-bold transition-all active:scale-95 flex items-center justify-center ${
                    isSelected
                      ? "bg-tone-accent text-white shadow-sm shadow-tone-accent/25"
                      : "bg-black/5 dark:bg-white/10 text-tone-soft hover:text-tone-ink"
                  }`}
                >
                  {m.label}
                </button>
              );
            })}
          </div>

          <Button
            variant="secondary"
            className="w-full min-h-[44px] font-bold text-xs"
            onClick={handleSimpleGenerate}
          >
            自动生成槽位（覆盖当前 {editTemplate.slots.length} 个槽位）
          </Button>
        </Card>
      ) : null}

      {/* 高级模式 / 槽位列表展示 */}
      <div className="space-y-3 mb-6">
        <div className="flex items-center justify-between">
          <SectionTitle hint={`共 ${editTemplate.slots.length} 个动作槽位`}>
            动作槽位定义
          </SectionTitle>
          <button
            onClick={handleAddSlot}
            className="text-xs font-bold text-tone-accent px-2 py-1 active:opacity-70"
          >
            + 添加槽位
          </button>
        </div>

        {editTemplate.slots.map((slot, idx) => {
          const byId = new Map(EXERCISE_LIBRARY.map((e) => [e.id, e]));
          const preferredExercises = slot.preferredIds
            .map((id) => byId.get(id))
            .filter(Boolean) as (typeof EXERCISE_LIBRARY)[0][];

          return (
            <Card key={slot.id || idx} className="p-3.5 space-y-2.5 border border-tone-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-tone-accent/10 text-tone-accent text-[11px] font-bold flex items-center justify-center">
                    {idx + 1}
                  </span>
                  <span className="text-[14px] font-bold text-tone-ink">{slot.description}</span>
                  {slot.isolationOnly ? <RiskTag tone="accent">仅孤立</RiskTag> : null}
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleMoveSlot(idx, "up")}
                    className="p-1 text-tone-soft hover:text-tone-ink text-xs font-bold active:scale-90"
                    title="上移"
                  >
                    ▲
                  </button>
                  <button
                    onClick={() => handleMoveSlot(idx, "down")}
                    className="p-1 text-tone-soft hover:text-tone-ink text-xs font-bold active:scale-90"
                    title="下移"
                  >
                    ▼
                  </button>
                  <button
                    onClick={() => handleRemoveSlot(idx)}
                    className="p-1 text-tone-danger text-xs font-bold active:scale-90"
                    title="删除"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* 高级编辑字段 */}
              {mode === "advanced" ? (
                <div className="space-y-2 pt-1 border-t border-tone-border text-xs">
                  <div>
                    <label className="text-[11px] font-medium text-tone-soft mb-0.5 block">槽位说明</label>
                    <input
                      value={slot.description}
                      onChange={(e) => handleUpdateSlot(idx, { description: e.target.value })}
                      className="w-full min-h-[36px] px-3 rounded-lg bg-black/5 dark:bg-white/10 text-tone-ink"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[11px] font-medium text-tone-soft mb-0.5 block">目标肌群</label>
                      <input
                        value={slot.muscle || ""}
                        onChange={(e) => handleUpdateSlot(idx, { muscle: e.target.value })}
                        placeholder="如：胸大肌"
                        className="w-full min-h-[36px] px-3 rounded-lg bg-black/5 dark:bg-white/10 text-tone-ink"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-medium text-tone-soft mb-0.5 block">动作模式</label>
                      <select
                        value={slot.pattern || ""}
                        onChange={(e) =>
                          handleUpdateSlot(idx, {
                            pattern: (e.target.value as MovementPattern) || undefined,
                          })
                        }
                        className="w-full min-h-[36px] px-2 rounded-lg bg-black/5 dark:bg-white/10 text-tone-ink"
                      >
                        <option value="">(未指定模式)</option>
                        {MOVEMENT_PATTERNS.map((p) => (
                          <option key={p.value} value={p.value}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-1">
                    <span className="text-[12px] text-tone-ink">只允许孤立动作</span>
                    <Toggle
                      checked={Boolean(slot.isolationOnly)}
                      onChange={(v) => handleUpdateSlot(idx, { isolationOnly: v })}
                    />
                  </div>
                </div>
              ) : null}

              {/* 首选动作展示与选择 */}
              <div className="pt-1.5">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[11px] font-semibold text-tone-soft">首选候选动作：</span>
                  <button
                    onClick={() => openPicker(idx)}
                    className="text-[11px] font-bold text-tone-accent active:opacity-70"
                  >
                    + 选择动作
                  </button>
                </div>

                <div className="flex items-center gap-1.5 flex-wrap">
                  {preferredExercises.map((e) => {
                    const avail = evaluateExerciseEligibility({ exercise: e, injury });
                    return (
                      <span
                        key={e.id}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold ${
                          e.lockedUntilMedicalClearance
                            ? "bg-tone-danger/10 text-tone-danger"
                            : !avail.isEnabledForCurrentInjury
                              ? "bg-tone-warn/10 text-tone-warn"
                              : "bg-tone-accent/10 text-tone-accent"
                        }`}
                      >
                        {e.nameZh}
                        {e.lockedUntilMedicalClearance ? " (锁)" : !avail.isEnabledForCurrentInjury ? " (限)" : ""}
                        <button
                          onClick={() => {
                            const next = slot.preferredIds.filter((id) => id !== e.id);
                            handleUpdateSlot(idx, { preferredIds: next });
                          }}
                          className="text-[11px] opacity-70 hover:opacity-100 ml-0.5"
                        >
                          ✕
                        </button>
                      </span>
                    );
                  })}
                  {preferredExercises.length === 0 ? (
                    <span className="text-[11px] text-tone-soft italic">
                      未指定首选（将按肌群模式在动作库中回退匹配）
                    </span>
                  ) : null}
                </div>
              </div>
            </Card>
          );
        })}

        {editTemplate.slots.length === 0 ? (
          <div className="text-center py-8 text-tone-danger text-xs font-semibold bg-tone-danger/5 rounded-2xl border border-tone-danger/20">
            ⚠️ 当前序列尚未添加任何动作槽位，无法生成训练计划。
          </div>
        ) : null}
      </div>

      {/* 校验错误展示 */}
      {validationErrors.length > 0 ? (
        <div className="p-3 mb-4 rounded-xl bg-tone-danger/10 border border-tone-danger/30 text-tone-danger text-xs space-y-1">
          <div className="font-bold">无法保存，请修正以下问题：</div>
          <ul className="list-disc pl-4 space-y-0.5">
            {validationErrors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* 底部保存按钮 */}
      <div className="sticky bottom-4 z-30 pt-2">
        <Button
          className="w-full h-[50px] font-bold text-base shadow-lg shadow-tone-accent/30"
          onClick={() => void handleSave()}
        >
          {saveSuccess ? "✓ 保存成功" : "保存序列修改"}
        </Button>
      </div>

      {/* 动作选择器抽屉 */}
      <Sheet
        open={showExercisePicker}
        onClose={() => setShowExercisePicker(false)}
        title={`选择首选动作（槽位 #${(activeSlotIdx ?? 0) + 1}）`}
      >
        <div className="p-4 space-y-3 max-h-[75vh] flex flex-col">
          <input
            value={pickerSearch}
            onChange={(e) => setPickerSearch(e.target.value)}
            placeholder="搜索动作 / 器械 / 肌群…"
            className="w-full min-h-[42px] px-3.5 rounded-xl bg-black/5 dark:bg-white/10 text-tone-ink text-xs font-semibold outline-none"
          />

          <div className="flex gap-1.5 flex-wrap">
            {["全部", "安全可用", "复合", "孤立"].map((f) => (
              <button
                key={f}
                onClick={() => setPickerFilter(f)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${
                  pickerFilter === f
                    ? "bg-tone-accent text-white"
                    : "bg-black/5 dark:bg-white/10 text-tone-soft"
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          <div className="overflow-y-auto space-y-2 flex-1 pr-1">
            {filteredPickerExercises.map((e) => {
              const avail = evaluateExerciseEligibility({ exercise: e, injury });
              const isSelected = currentSlot?.preferredIds.includes(e.id) ?? false;
              const isLocked = e.lockedUntilMedicalClearance;

              return (
                <div
                  key={e.id}
                  onClick={() => togglePreferredExercise(e.id)}
                  className={`p-3 rounded-xl border transition-all cursor-pointer flex items-center justify-between ${
                    isSelected
                      ? "border-tone-accent bg-tone-accent/10"
                      : "border-tone-border bg-black/[0.02] dark:bg-white/[0.03]"
                  }`}
                >
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-bold text-tone-ink">{e.nameZh}</span>
                      {isLocked ? (
                        <RiskTag tone="danger">医疗锁</RiskTag>
                      ) : !avail.isEnabledForCurrentInjury ? (
                        <RiskTag tone="warn">受限</RiskTag>
                      ) : (
                        <RiskTag tone="good">可用</RiskTag>
                      )}
                    </div>
                    <div className="text-[11px] text-tone-soft">
                      {e.primaryMuscles.join(" · ")} · {e.compoundType === "compound" ? "复合" : "孤立"}
                    </div>
                  </div>

                  <div className="w-5 h-5 rounded-full border border-tone-border flex items-center justify-center text-xs font-bold text-tone-accent">
                    {isSelected ? "✓" : ""}
                  </div>
                </div>
              );
            })}
          </div>

          <Button className="w-full min-h-[44px] font-bold text-xs" onClick={() => setShowExercisePicker(false)}>
            完成选择
          </Button>
        </div>
      </Sheet>

      {/* 恢复默认二次确认抽屉 */}
      <Sheet open={confirmReset} onClose={() => setConfirmReset(false)} title="确认恢复默认方案？">
        <div className="p-4 space-y-3">
          <p className="text-xs text-tone-soft leading-relaxed">
            此操作将把所有训练序列重置为系统默认的 5 个推荐分化方案（A 胸三头 / B 背二头 / C 肩核心 / D 上肢综合 / E 可选轻训）。你自定义的序列改动将被覆盖。
          </p>
          <div className="grid grid-cols-2 gap-2 pt-2">
            <Button variant="ghost" onClick={() => setConfirmReset(false)}>
              取消
            </Button>
            <Button
              className="bg-tone-danger hover:bg-tone-danger/90 text-white font-bold"
              onClick={() => void handleResetDefaults()}
            >
              确认恢复默认
            </Button>
          </div>
        </div>
      </Sheet>

      {/* 删除序列二次确认抽屉 */}
      <Sheet open={confirmDelete} onClose={() => setConfirmDelete(false)} title={`确认删除序列 ${editTemplate.code}？`}>
        <div className="p-4 space-y-3">
          <p className="text-xs text-tone-soft leading-relaxed">
            删除序列「{editTemplate.name}」后，该序列将不再进入今日训练轮转。
          </p>
          <div className="grid grid-cols-2 gap-2 pt-2">
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
              取消
            </Button>
            <Button
              className="bg-tone-danger hover:bg-tone-danger/90 text-white font-bold"
              onClick={() => void handleDeleteTemplate()}
            >
              确认删除
            </Button>
          </div>
        </div>
      </Sheet>
    </div>
  );
}
